import { defineStore } from "pinia";
import { computed } from "vue";

import { useMyDriveComposable } from "./myDrive";
import { useSensitivityLabelsComposable } from "./sensitivityLabels";
import { useQuickshareComposable } from "./quickshare";
import { useDriveItemsComposable } from "./driveItems";
import { usePermissionsComposable } from "./permissions";
import { useSharedDrivesComposable } from "./sharedDrives";
import { useBreadCrumbComposable } from "./breadCrumb";
import { useTemplatesComposable } from "./templatesComposable";
import { useMyFiles } from "./myFiles";
import { useDriveItemsHierarchyComposable } from "./driveItemsHierarchy";
import { useConfidentialDocumentsComposable } from "./confidentialDocuments";
import { useControlledAreaComposable } from "./controlledArea";
import { useDownloadProgressComposable } from "./downloadProgress";
import { useDocumentFunctionsComposable } from "./documentFunctions";
import { useLegacyComposable } from "./legacy";

// SharePoint sharing config used by calculateRootOrItemId
interface SharedDriveEntry {
  driveId: string;
  itemId: string;
  [key: string]: unknown;
}

export const useDocumentStore = defineStore("documents", () => {
  const myDrive = useMyDriveComposable();
  const sensitivityLabels = useSensitivityLabelsComposable();
  const downloadProgress = useDownloadProgressComposable();
  const quickshare = useQuickshareComposable({
    downloadProgressState: downloadProgress.downloadProgressState
  });
  const driveItems = useDriveItemsComposable({
    getCalculateDriveItemsRoot: () => driveItemsHierarchy.calculateDriveItemsRoot
  });
  const permissions = usePermissionsComposable({
    calculateRootOrDriveId: driveId => myDrive.calculateRootOrDriveId.value(driveId),
    getCalculateRootOrItemId: () => calculateRootOrItemId.value,
    loadDriveItem: driveItems.loadDriveItem
  });
  const sharedDrives = useSharedDrivesComposable({
    myDriveRootId: myDrive.myDriveRootId,
    myItemPermission: permissions.myItemPermission
  });
  const breadCrumb = useBreadCrumbComposable({
    getCalculateDriveItemsHierarchy: () => driveItemsHierarchy.calculateDriveItemsHierarchy
  });
  const templates = useTemplatesComposable({
    loadDriveItem: driveItems.loadDriveItem,
    getAddSpecialItem: () => confidentialDocuments.addSpecialItem
  });
  const myFiles = useMyFiles({
    driveMap: driveItems.driveMap,
    getLoadHiddenDriveItems: () => documentFunctions.loadHiddenDriveItems,
    hiddenSharedFiles: computed(() => documentFunctions.hiddenSharedFilesState.value)
  });
  const driveItemsHierarchy = useDriveItemsHierarchyComposable({
    loadDriveItemWithoutDecorationgWithNCTProps: driveItems.loadDriveItemWithoutDecorationgWithNCTProps,
    setNCTMetaOnStoreDriveItems: driveItems.setNCTMetaOnStoreDriveItems,
    loadSharedDrives: sharedDrives.loadSharedDrives,
    sharedDrives: sharedDrives.sharedDrives,
    getLoadControlledAreaDefinitions: () => controlledArea.loadControlledAreaDefinitions,
    getControlledAreaFromDriveIdAndItemId: () => controlledArea.controlledAreaFromDriveIdAndItemId.value
  });
  const confidentialDocuments = useConfidentialDocumentsComposable({
    driveItem: driveItems.driveItem,
    calculateDriveItemsHierarchy: driveItemsHierarchy.calculateDriveItemsHierarchy
  });
  const controlledArea = useControlledAreaComposable({
    driveItem: driveItems.driveItem,
    loadDriveItem: driveItems.loadDriveItem,
    reloadDriveItem: driveItems.reloadDriveItem,
    reloadDriveItemFromPath: driveItems.reloadDriveItemFromPath,
    specialItemObjectForDriveItem: confidentialDocuments.specialItemObjectForDriveItem,
    loadDocumentPermissions: permissions.loadDocumentPermissions,
    getIsConfidentialDocumentShareToAulaForUserIdOnDriveId: () =>
      documentFunctions.isConfidentialDocumentShareToAulaForUserIdOnDriveId.value,
    getIsDocumentJournalizing: () => documentFunctions.isDocumentJournalizing.value,
    getIsDocumentLocking: () => documentFunctions.isDocumentLocking.value,
    getAppConfidentialId: () => documentFunctions.appConfidentialId.value,
    getReloadConfidentialDocumentsSharedToAulaForUserOnDrive: () =>
      documentFunctions.reloadConfidentialDocumentsSharedToAulaForUserOnDrive
  });
  const documentFunctions = useDocumentFunctionsComposable({
    driveItem: driveItems.driveItem,
    driveItemChildren: driveItems.driveItemChildren,
    parentDriveItem: driveItems.parentDriveItem,
    loadDriveItem: driveItems.loadDriveItem,
    reloadDriveItem: driveItems.reloadDriveItem,
    myDriveRootId: myDrive.myDriveRootId,
    calculateDriveIdOrMyDrive: myDrive.calculateDriveIdOrMyDrive,
    reloadDocumentPermissions: permissions.reloadDocumentPermissions,
    sharedDrives: sharedDrives.sharedDrives,
    myItemPermission: permissions.myItemPermission,
    controlledAreaKeysFromDriveIdAndItemId: controlledArea.controlledAreaKeysFromDriveIdAndItemId,
    controlledAreaConfidentialId: controlledArea.controlledAreaConfidentialId,
    downloadProgressState: downloadProgress.downloadProgressState,
    getCalculateRootOrItemId: () => calculateRootOrItemId.value,
    getReload: () => reload
  });
  const legacy = useLegacyComposable({
    calculateDriveIdOrMyDrive: myDrive.calculateDriveIdOrMyDrive,
    myDriveRootId: myDrive.myDriveRootId,
    myItemPermission: permissions.myItemPermission,
    sharedWithPermission: permissions.sharedWithPermission,
    reloadDocumentPermissions: permissions.reloadDocumentPermissions,
    reloadSharedWithPermissions: permissions.reloadSharedWithPermissions,
    loadDriveItem: driveItems.loadDriveItem,
    getCalculateRootOrItemId: () => calculateRootOrItemId.value
  });

  const calculateRootOrItemId = computed(() => (driveId: string, itemId: string) => {
    if (itemId) {
      return itemId;
    }
    const sharedDrivesForDrive = sharedDrives.sharedDrives.value.filter(
      (spf: SharedDriveEntry) => spf.driveId === driveId
    );
    if (sharedDrivesForDrive.length !== 0) {
      return sharedDrivesForDrive[0].itemId;
    }
    return legacy.driveRootItemIdMap.value && legacy.driveRootItemIdMap.value[driveId]
      ? legacy.driveRootItemIdMap.value[driveId]
      : "root";
  });

  // -------------------- actions: drive items --------------------
  const reload = async (driveId: string, itemId: string) => {
    const combiDriveDef = controlledArea.controlledAreaKeysFromDriveIdAndItemId.value(driveId, itemId);
    if (combiDriveDef) {
      controlledArea.reloadCombiDriveFolder(combiDriveDef.system, combiDriveDef.entity, combiDriveDef.group, itemId);
    }
    return driveItems.reloadDriveItem(driveId, itemId);
  };

  // -------------------- return surface --------------------
  return {
    ...myDrive,
    ...sensitivityLabels,
    ...quickshare,
    ...driveItems,
    ...permissions,
    ...sharedDrives,
    ...breadCrumb,
    ...templates,
    ...myFiles,
    ...driveItemsHierarchy,
    ...confidentialDocuments,
    ...controlledArea,
    ...downloadProgress,
    ...documentFunctions,
    ...legacy,

    // getters
    calculateRootOrItemId,

    // actions
    reload
  };
});
